import React, { FC } from 'react';
import { Polyline, useMap } from 'react-leaflet';
import { Abgangsleitung, Waypoint } from '../../../types/network';
import L, { LeafletMouseEvent, LatLngTuple } from 'leaflet';
import { useNetworkUIStore } from '../../../store/networkUIStore';
import { selectWithCoLocation } from '../elementHitTest';
import { useNetworkElementsStore } from '../../../store/networkElementsStore';
import { useShallow } from 'zustand/react/shallow';
import { useSettingsStore } from '../../../store/settingsStore';
import { getCableStyle } from '../../../types/cable';
import type { LoadLevelThresholds } from '../../../services/settingsService';
import { loadingMapColor } from '../../../utils/loadingConstants';

// Polyline with renderer prop for hybrid Canvas/SVG rendering (CSS animations need SVG)
type RenderedPolylineProps = React.ComponentProps<typeof Polyline> & { renderer?: L.Renderer };
const RenderedPolyline = Polyline as React.ComponentType<RenderedPolylineProps>;

interface AbgangsleitungLineProps {
  element: Abgangsleitung;
  isHighlighted: boolean;
  svgRenderer?: L.Renderer;
}

// === PURE FUNCTIONS (outside component for performance) ===

const getLoadClass = (loadPercentage: number, thresholds: LoadLevelThresholds): string => {
  if (loadPercentage >= thresholds.orange) return 'load-high';
  if (loadPercentage >= thresholds.green) return 'load-medium';
  return 'load-low';
};

const waypointArrayToPositions = (waypoints?: Waypoint[] | null): LatLngTuple[] => {
  if (!Array.isArray(waypoints)) return [];
  const coords: LatLngTuple[] = [];
  for (const waypoint of waypoints) {
    if (!waypoint || typeof waypoint !== 'object') continue;
    const lat = Number(waypoint.lat);
    const lng = Number(waypoint.lng);
    if (!isNaN(lat) && !isNaN(lng)) {
      coords.push([lat, lng]);
    }
  }
  return coords;
};

const distanceBetween = (a: LatLngTuple, b: LatLngTuple): number => {
  const dLat = a[0] - b[0];
  const dLng = a[1] - b[1];
  return Math.sqrt(dLat * dLat + dLng * dLng);
};

const hasSegmentPower = (data: any): boolean => {
  if (!data) return false;
  if (typeof data.powerKW === 'number') {
    return Math.abs(data.powerKW) > 0.001;
  }
  if (typeof data.currentA === 'number') {
    return Math.abs(data.currentA) > 0.001;
  }
  return false;
};

// Ring circuit color palette — each unique ringCircuitId maps to a distinct color
const RING_COLORS = ['#00CED1', '#FF69B4', '#7B68EE', '#FFA500', '#00FA9A', '#DC143C', '#1E90FF', '#FFD700'];

const getRingColor = (ringCircuitId: string): string => {
  let hash = 0;
  for (let i = 0; i < ringCircuitId.length; i++) {
    hash = ((hash << 5) - hash + ringCircuitId.charCodeAt(i)) | 0;
  }
  return RING_COLORS[Math.abs(hash) % RING_COLORS.length];
};

// === MAIN COMPONENT ===

export const AbgangsleitungLine: FC<AbgangsleitungLineProps> = React.memo(({ element, isHighlighted, svgRenderer }) => {
  const map = useMap();
  // === STORE SUBSCRIPTIONS (optimized) ===
  const selectedElementId = useNetworkUIStore(state => state.selectedElement?.id);

  // CRITICAL: Only subscribe to THIS cable's segment data, not entire store
  const segmentData = useNetworkElementsStore(useShallow(state => {
    const data: Record<string, any> = {};
    for (const seg of element.segments) {
      if (state.segments[seg.id]) {
        data[seg.id] = state.segments[seg.id];
      }
    }
    return data;
  }));
  const getElementById = useNetworkElementsStore(state => state.getElement);

  const resolveElementCoords = React.useCallback((elementId?: string | null): LatLngTuple | null => {
    if (!elementId) return null;
    const foundElement = getElementById(elementId);
    const coords = foundElement?.coordinates;
    if (!coords) return null;
    const lat = Number(coords.lat);
    const lng = Number(coords.lng);
    if (isNaN(lat) || isNaN(lng)) return null;
    return [lat, lng];
  }, [getElementById]);

  const thresholds = useSettingsStore(s => s.thresholds.cableLoadLevels);
  const ringCircuitHighlighting = useSettingsStore(s => s.settings?.mapSettings?.ringCircuitHighlighting ?? true);

  // === DERIVED STATE ===
  const isSelected = selectedElementId === element.id;
  const cableStyle = getCableStyle(element.cableType);

  // === POSITIONS CALCULATION ===
  const positions = React.useMemo(() => waypointArrayToPositions(element.waypoints), [element.waypoints]);

  // === SEGMENT-LEVEL OVERLAYS ===
  const segmentOverlays = React.useMemo(() => {
    const determineDirectionClass = (segmentPositions: LatLngTuple[], data: any): string => {
      if (!data || segmentPositions.length < 2) return 'power-flow-line';

      const fromId = data.fromBusElementId;
      const toId = data.toBusElementId;
      const fromCoords = resolveElementCoords(fromId);
      const toCoords = resolveElementCoords(toId);
      const start = segmentPositions[0];
      const end = segmentPositions[segmentPositions.length - 1];

      if (fromCoords && toCoords) {
        const forwardScore = distanceBetween(start, fromCoords) + distanceBetween(end, toCoords);
        const reverseScore = distanceBetween(end, fromCoords) + distanceBetween(start, toCoords);
        return forwardScore <= reverseScore ? 'power-flow-line' : 'power-flow-reverse';
      }

      if (typeof data.powerKW === 'number' && data.powerKW < 0) {
        return 'power-flow-reverse';
      }

      return 'power-flow-line';
    };

    return element.segments.reduce<Array<{ id: string; positions: LatLngTuple[]; directionClass: string; hasPower: boolean; loadingPercent: number }>>((acc, segment) => {
      let segmentPositions = waypointArrayToPositions(segment.waypoints);
      if (segmentPositions.length < 2) {
        const fallbackWaypoints: Waypoint[] = [];
        if (segment.startCoordinate) fallbackWaypoints.push(segment.startCoordinate);
        if (segment.endCoordinate) fallbackWaypoints.push(segment.endCoordinate);
        segmentPositions = waypointArrayToPositions(fallbackWaypoints);
      }

      if (segmentPositions.length < 2) {
        return acc;
      }

      const data = segmentData[segment.id];
      acc.push({
        id: segment.id,
        positions: segmentPositions,
        directionClass: determineDirectionClass(segmentPositions, data),
        hasPower: hasSegmentPower(data),
        loadingPercent: data?.loadingPercent ?? 0
      });
      return acc;
    }, []);
  }, [element.segments, resolveElementCoords, segmentData]);

  const hasAnyPowerData = React.useMemo(() => segmentOverlays.some(seg => seg.hasPower), [segmentOverlays]);

  // === VISUAL PROPERTIES ===
  const showPowerFlowAnimation = element.isEnabled && hasAnyPowerData;

  // Weight calculations
  const baseWeight = cableStyle.weight * 1.5;
  const selectedWeight = cableStyle.weight * 2;
  const currentWeight = (isSelected || isHighlighted) ? selectedWeight : baseWeight;

  // === EVENT HANDLERS ===
  const handleClick = React.useCallback((e: LeafletMouseEvent) => {
    L.DomEvent.stopPropagation(e);
    e.originalEvent.preventDefault();
    e.originalEvent.stopPropagation();
    // Selects this cable and records everything else under the cursor
    // (co-located trench cables, stacked markers) for the details panel.
    selectWithCoLocation(map, e, element);
  }, [element, map]);

  // Early exit if no positions (after all hooks)
  if (positions.length === 0) return null;

  // === RENDER ===
  return (
    <>
      {/* 1. INVISIBLE CLICK TARGET - Covers gaps between segments */}
      <Polyline
        positions={positions}
        pathOptions={{
          color: '#000000',
          weight: currentWeight + 6,
          opacity: 0.001,
          lineCap: 'round',
          lineJoin: 'round'
        }}
        eventHandlers={{
          click: handleClick,
          mousedown: (e: LeafletMouseEvent) => L.DomEvent.stopPropagation(e)
        }}
      />

      {/* 2. RING CIRCUIT INDICATOR - Dashed outline behind cable for ring circuits */}
      {ringCircuitHighlighting && element.isRingCircuit && element.ringCircuitId && (
        <Polyline
          positions={positions}
          pathOptions={{
            color: getRingColor(element.ringCircuitId),
            weight: currentWeight + 6,
            opacity: 0.85,
            dashArray: '10 6',
            lineCap: 'round',
            lineJoin: 'round',
            interactive: false,
          }}
        />
      )}

      {/* 3. PER-SEGMENT BASE LINES - Each segment colored by its own load */}
      {segmentOverlays.map(overlay => {
        const segColor = element.isEnabled ? loadingMapColor(overlay.loadingPercent, thresholds) : '#999999';
        return (
          <Polyline
            key={`base-${overlay.id}`}
            positions={overlay.positions}
            pathOptions={{
              color: segColor,
              weight: currentWeight,
              opacity: 0.8,
              lineCap: 'round',
              lineJoin: 'round'
            }}
            eventHandlers={{
              click: handleClick,
              mousedown: (e: LeafletMouseEvent) => L.DomEvent.stopPropagation(e)
            }}
          />
        );
      })}

      {/* 4. POWER FLOW OVERLAY - White animated dashed line on top */}
      {showPowerFlowAnimation && segmentOverlays
        .filter(overlay => overlay.hasPower)
        .map(overlay => {
          const segLoadClass = getLoadClass(overlay.loadingPercent, thresholds);
          return (
            <RenderedPolyline
              key={`${overlay.id}-${overlay.directionClass}-${segLoadClass}`}
              positions={overlay.positions}
              renderer={svgRenderer}
              className={`${overlay.directionClass} ${segLoadClass}`}
              pathOptions={{
                pane: 'overlayPane',
                color: '#ffffff',
                weight: 3,
                opacity: 0.8,
                dashArray: '10 6',
                lineCap: 'round',
                lineJoin: 'round',
                interactive: false
              }}
              eventHandlers={{
                add: (e: any) => {
                  const applyClasses = () => {
                    const path = e.target.getElement?.();
                    if (path) {
                      path.classList.add(overlay.directionClass, segLoadClass);
                      path.style.pointerEvents = 'none';
                    }
                  };
                  // Apply immediately + retry next frame for production robustness
                  applyClasses();
                  requestAnimationFrame(applyClasses);
                }
              }}
            />
          );
        })}

      {/* 5. SELECTION/HIGHLIGHT GLOW - Golden glow when selected */}
      {(isSelected || isHighlighted) && (
        <Polyline
          positions={positions}
          pathOptions={{
            color: '#FFD700',
            weight: cableStyle.weight * 4,
            opacity: isHighlighted ? 0.3 : 0.2,
            lineCap: 'round',
            lineJoin: 'round',
            interactive: false,  // Don't intercept clicks
            className: isHighlighted ? 'animate-pulse' : undefined
          }}
        />
      )}
    </>
  );
}, (prevProps, nextProps) => {
  // Custom comparison: only re-render if these specific properties changed
  return (
    prevProps.element.id === nextProps.element.id &&
    prevProps.element.isEnabled === nextProps.element.isEnabled &&
    prevProps.element.cableType === nextProps.element.cableType &&
    prevProps.element.waypoints === nextProps.element.waypoints &&
    prevProps.element.segments === nextProps.element.segments &&
    prevProps.element.sourceId === nextProps.element.sourceId &&
    prevProps.element.targetId === nextProps.element.targetId &&
    prevProps.element.isRingCircuit === nextProps.element.isRingCircuit &&
    prevProps.element.ringCircuitId === nextProps.element.ringCircuitId &&
    prevProps.isHighlighted === nextProps.isHighlighted
  );
});

AbgangsleitungLine.displayName = 'AbgangsleitungLine';
